import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import i18next from 'i18next';
import { useEmailAvailableMutation } from '../../../api/api';
import analyticsTracker from '../../../common/analytics';
import {
  appEnvironment,
  appAutoCompleteData,
} from '../../../common/environment';
import { useMobile } from '../../../common/hooks';
import i18n from '../../../i18n';
import validator from '../../../onboarding/common/validator';
import { checkoutActions } from '../../../redux/checkout';
import { addDiscountCodeV2 } from '../../../redux/checkoutActions';
import { onboardingActions } from '../../../redux/onboarding';
import { OnboardingV2Step } from '../../../redux/onboardingTypes';
import { useAppDispatch } from '../../../redux/store';
import {
  getMemberTermsURI,
  getPrivacyNoticeURI,
} from '../../../common/legalConstants';

export const useSignUpForm = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const mobile = useMobile();
  const [params] = useSearchParams();
  const [checkEmailAvailability] = useEmailAvailableMutation();

  const isSyd = appEnvironment.theme.appName.toLowerCase() === 'syd';
  const isJa = (i18n.resolvedLanguage ?? i18n.language)?.startsWith('ja');

  // Fields
  const [name, setName] = useState(
    appEnvironment.autocomplete ? appAutoCompleteData.name : ''
  );
  const [lastName, setLastName] = useState(
    appEnvironment.autocomplete ? appAutoCompleteData.lastName : ''
  );
  const [coupon, setCoupon] = useState(
    appEnvironment.autocomplete
      ? appAutoCompleteData.coupon
      : params.get('code') || ''
  );
  const [email, setEmail] = useState(
    appEnvironment.autocomplete ? appAutoCompleteData.email : ''
  );
  const [password, setPassword] = useState(
    appEnvironment.autocomplete ? appAutoCompleteData.password : ''
  );
  const [passwordOn, setPasswordOn] = useState(true);

  // Errors
  const [nameError, setNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [couponError, setCouponError] = useState('');
  const [emailError, setEmailError] = useState('');

  // Consents
  const [tcsAgreed, setTcsAgreed] = useState(appEnvironment.autocomplete);
  const [healthDataConsent, setHealthDataConsent] = useState(
    appEnvironment.autocomplete
  );
  const [researchConsent, setResearchConsent] = useState(
    appEnvironment.autocomplete
  );

  const termsURI = useMemo(
    () => getMemberTermsURI({ isJa, language: i18next.language }),
    [isJa]
  );
  const privacyNoticeURI = useMemo(
    () => getPrivacyNoticeURI({ isJa, language: i18next.language }),
    [isJa]
  );

  const hasErrors = useMemo(() => {
    const trimmedEmail = email.trim();
    const consentValid = isSyd ? healthDataConsent : tcsAgreed;

    return (
      name.length === 0 ||
      lastName.length === 0 ||
      trimmedEmail.length === 0 ||
      !validator.validEmail(trimmedEmail) ||
      password.length === 0 ||
      !validator.passwordLength(password) ||
      !validator.passwordHasSpecialCharacters(password) ||
      !validator.passwordHasUppercaseAndLowercase(password) ||
      !validator.passwordHasDigit(password) ||
      emailError.length > 0 ||
      !consentValid
    );
  }, [
    email,
    emailError.length,
    healthDataConsent,
    isSyd,
    lastName.length,
    name.length,
    password,
    tcsAgreed,
  ]);

  useEffect(() => {
    dispatch(onboardingActions.setButtonEnabled(!hasErrors));
  }, [dispatch, hasErrors]);

  const handleBlurEmail = () => {
    if (!email) {
      return;
    }
    const trimmed = email.trim();
    if (trimmed !== email) {
      setEmail(trimmed);
    }

    if (!validator.validEmail(trimmed)) {
      if (!validator.emailHasAt(trimmed)) {
        setEmailError(t('emailMissingAt'));
      } else if (
        validator.emailHasLocalPart(trimmed) &&
        !validator.emailHasDomain(trimmed)
      ) {
        setEmailError(t('emailMissingDomain'));
      } else if (validator.emailHasInvalidCharacters(trimmed)) {
        setEmailError(t('emailInvalidCharacters'));
      } else {
        setEmailError(t('emailInvalidFallback'));
      }
    }
  };

  const submit = async () => {
    if (hasErrors) {
      return {};
    }
    const finalEmail = email.trim().toLowerCase();

    const { available } = await checkEmailAvailability({
      email: finalEmail,
    }).unwrap();
    if (!available) {
      setEmailError(t('emailIsAlreadyTaken'));
      return {};
    }

    try {
      Sentry.setUser({ email: finalEmail, name, lastName });

      await dispatch(
        addDiscountCodeV2(
          coupon.length > 0 ? coupon : appEnvironment.defaultCoupon
        )
      ).unwrap();

      dispatch(
        checkoutActions.setPersonalData({
          name,
          lastName,
          email: finalEmail,
          password,
          code: coupon,
        })
      );
      dispatch(
        onboardingActions.setOnboardingStep(OnboardingV2Step.SECOND_FORM)
      );
      analyticsTracker.track('Web 1st step completed', { mobile });
    } catch {
      setCouponError(t('invalidCoupon'));
    }

    return {};
  };

  return {
    isSyd,
    isJa,
    mobile,
    termsURI,
    privacyNoticeURI,
    name,
    setName,
    nameError,
    setNameError,
    lastName,
    setLastName,
    lastNameError,
    setLastNameError,
    coupon,
    setCoupon,
    couponError,
    setCouponError,
    email,
    setEmail,
    emailError,
    setEmailError,
    handleBlurEmail,
    password,
    setPassword,
    passwordOn,
    setPasswordOn,
    healthDataConsent,
    setHealthDataConsent,
    researchConsent,
    setResearchConsent,
    tcsAgreed,
    setTcsAgreed,
    submit,
    t,
  };
};
